import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CalendarClock,
  CheckSquare,
  FolderInput,
  Square,
  Tag as TagIcon,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react';
import { useTaskCompletion } from '../../hooks/useTaskCompletion';
import { toast } from 'sonner';
import {
  useUpdateTask,
  useMoveTask,
  useTaskSections,
  type Task,
} from '../../hooks/queries/useTasks';
import {
  useActiveCollaborators,
  useAssignTask,
  useTasksAssignments,
} from '../../hooks/queries/useTaskCollaboration';
import { useTaskTags, useTaskTagsFor, useSetTaskTags } from '../../hooks/queries/useTaskTags';
import { useEffectiveTaskFields } from '../../hooks/useEffectiveTaskFields';
import { useUndoableBulkDelete } from '../../hooks/useUndoableTaskDelete';
import { resolveBulkCompleteAction } from '../../lib/tasks/taskSelection';
import { mergeDueDate, clearDue } from '../../lib/tasks/taskReschedule';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Avatar } from '../ui/Avatar';

interface TasksBulkBarProps {
  selectedTasks: Task[];
  activeDossierId: string | null;
  onClear: () => void;
}

const BUTTON_CLASS =
  'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-cime-text-primary hover:bg-[#fbfaf7] transition';

const POPOVER_CLASS = 'w-auto p-1 rounded-lg border-cime-border bg-white shadow-lg';

function isoLocal(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function TasksBulkBar({ selectedTasks, activeDossierId, onClear }: TasksBulkBarProps) {
  const fields = useEffectiveTaskFields();
  const completion = useTaskCompletion();
  const requestDeleteMany = useUndoableBulkDelete();

  const [assignOpen, setAssignOpen] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [dueOpen, setDueOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);

  const taskIds = selectedTasks.map((t) => t.id);

  const collaborators = useActiveCollaborators();
  const assignments = useTasksAssignments(taskIds);
  const assignTask = useAssignTask();

  const tags = useTaskTags();
  const tagsFor = useTaskTagsFor(taskIds);
  const setTaskTags = useSetTaskTags();

  const updateTask = useUpdateTask();

  const sections = useTaskSections(activeDossierId);
  const moveTask = useMoveTask();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      // Un popover Radix ouvert consomme Échap pour se fermer — ne pas vider
      // la sélection en même temps.
      if (document.querySelector('[data-radix-popper-content-wrapper]')) return;
      onClear();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClear]);

  const n = selectedTasks.length;
  const action = resolveBulkCompleteAction(selectedTasks);

  async function handleToggleComplete() {
    const targets = selectedTasks.filter((t) =>
      action === 'complete' ? !t.is_completed : t.is_completed,
    );
    if (action === 'complete') await completion.finishMany(targets);
    else await completion.reopenMany(targets);
  }

  function handleDelete() {
    requestDeleteMany(selectedTasks.map(({ id, content }) => ({ id, content })));
    onClear();
  }

  async function handleAssign(collabId: string) {
    const targets = selectedTasks.filter(
      (t) => !(assignments.data?.get(t.id) ?? []).some((a) => a.id === collabId),
    );
    const results = await Promise.allSettled(
      targets.map((t) => assignTask.mutateAsync({ taskId: t.id, assigneeId: collabId })),
    );
    if (results.some((r) => r.status === 'rejected')) {
      toast.error("Certaines tâches n'ont pas pu être modifiées");
    }
    setAssignOpen(false);
    onClear();
  }

  async function handleTag(tagId: string) {
    const results = await Promise.allSettled(
      selectedTasks.map((t) => {
        const current = (tagsFor.data?.[t.id] ?? []).map((tag) => tag.id);
        if (current.includes(tagId)) return Promise.resolve();
        return setTaskTags.mutateAsync({ taskId: t.id, tagIds: [...current, tagId] });
      }),
    );
    if (results.some((r) => r.status === 'rejected')) {
      toast.error("Certaines tâches n'ont pas pu être modifiées");
    }
    setTagOpen(false);
    onClear();
  }

  async function applyDueToDay(isoDay: string) {
    const results = await Promise.allSettled(
      selectedTasks.map((t) =>
        updateTask.mutateAsync({ taskId: t.id, updates: mergeDueDate(t, isoDay) }),
      ),
    );
    if (results.some((r) => r.status === 'rejected')) {
      toast.error("Certaines tâches n'ont pas pu être modifiées");
    }
    setDueOpen(false);
    onClear();
  }

  function pickDueDay(offsetDays: number) {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    void applyDueToDay(isoLocal(d));
  }

  async function handleClearDue() {
    const updates = clearDue();
    const results = await Promise.allSettled(
      selectedTasks.map((t) => updateTask.mutateAsync({ taskId: t.id, updates })),
    );
    if (results.some((r) => r.status === 'rejected')) {
      toast.error("Certaines tâches n'ont pas pu être modifiées");
    }
    setDueOpen(false);
    onClear();
  }

  async function handleMove(targetSectionId: string | null) {
    let failed = false;
    for (const t of selectedTasks) {
      try {
        await moveTask.mutateAsync({ taskId: t.id, targetSectionId, beforeTaskId: null });
      } catch {
        failed = true;
      }
    }
    if (failed) toast.error("Certaines tâches n'ont pas pu être modifiées");
    setMoveOpen(false);
    onClear();
  }

  return createPortal(
    <div
      data-cime-modal
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 rounded-xl border border-cime-border bg-white shadow-lg px-3 py-2"
    >
      <span className="text-sm font-semibold text-cime-text-primary px-1">
        {n} sélectionnée{n > 1 ? 's' : ''}
      </span>
      <span className="w-px h-5 bg-cime-border mx-1" aria-hidden />
      <button type="button" className={BUTTON_CLASS} onClick={handleToggleComplete}>
        {action === 'complete' ? (
          <CheckSquare className="w-4 h-4" />
        ) : (
          <Square className="w-4 h-4" />
        )}
        {action === 'complete' ? 'Terminer' : 'Rouvrir'}
      </button>

      <Popover open={assignOpen} onOpenChange={setAssignOpen}>
        <PopoverTrigger asChild>
          <button type="button" className={BUTTON_CLASS}>
            <UserPlus className="w-4 h-4" />
            Assigner
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="center"
          side="top"
          data-cime-modal
          onClick={(e) => e.stopPropagation()}
          className={`${POPOVER_CLASS} min-w-[200px] max-h-[min(20rem,var(--radix-popover-content-available-height))] overflow-y-auto`}
        >
          <div className="flex flex-col gap-0.5">
            {(collaborators.data ?? []).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => handleAssign(c.id)}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded text-sm text-left text-cime-text-primary hover:bg-[#fbfaf7]"
              >
                <Avatar name={c.full_name} src={c.avatar_url} size="xs" />
                <span className="truncate">{c.full_name}</span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {fields.tags && (
      <Popover open={tagOpen} onOpenChange={setTagOpen}>
        <PopoverTrigger asChild>
          <button type="button" className={BUTTON_CLASS}>
            <TagIcon className="w-4 h-4" />
            Étiquette
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="center"
          side="top"
          data-cime-modal
          onClick={(e) => e.stopPropagation()}
          className={`${POPOVER_CLASS} min-w-[200px] max-h-[min(20rem,var(--radix-popover-content-available-height))] overflow-y-auto`}
        >
          <div className="flex flex-col gap-0.5">
            {(tags.data ?? []).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => handleTag(t.id)}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded text-sm text-left text-cime-text-primary hover:bg-[#fbfaf7]"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: t.color }}
                  aria-hidden
                />
                <span className="truncate">{t.name}</span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      )}

      <Popover open={dueOpen} onOpenChange={setDueOpen}>
        <PopoverTrigger asChild>
          <button type="button" className={BUTTON_CLASS}>
            <CalendarClock className="w-4 h-4" />
            Échéance
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="center"
          side="top"
          data-cime-modal
          onClick={(e) => e.stopPropagation()}
          className={`${POPOVER_CLASS} p-2`}
        >
          <div className="flex flex-col gap-1 min-w-[180px]">
            {(
              [
                ["Aujourd'hui", 0],
                ['Demain', 1],
                ['+1 semaine', 7],
              ] as [string, number][]
            ).map(([label, offset]) => (
              <button
                key={label}
                type="button"
                onClick={() => pickDueDay(offset)}
                className="px-2.5 py-1.5 rounded text-xs font-medium text-left text-cime-text-primary hover:bg-[#fbfaf7]"
              >
                {label}
              </button>
            ))}
            <input
              type="date"
              onChange={(e) => {
                if (e.target.value) void applyDueToDay(e.target.value);
              }}
              className="px-2 py-1.5 rounded border border-cime-border text-xs text-cime-text-primary"
              aria-label="Choisir une date"
            />
            <button
              type="button"
              onClick={handleClearDue}
              className="px-2.5 py-1.5 rounded text-xs font-medium text-left text-cime-urgent hover:bg-[#fbfaf7]"
            >
              Retirer l'échéance
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {fields.sections && activeDossierId != null && (
        <Popover open={moveOpen} onOpenChange={setMoveOpen}>
          <PopoverTrigger asChild>
            <button type="button" className={BUTTON_CLASS}>
              <FolderInput className="w-4 h-4" />
              Déplacer
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="center"
            side="top"
            data-cime-modal
            onClick={(e) => e.stopPropagation()}
            className={`${POPOVER_CLASS} min-w-[200px] max-h-[min(20rem,var(--radix-popover-content-available-height))] overflow-y-auto`}
          >
            <div className="flex flex-col gap-0.5">
              <button
                type="button"
                onClick={() => handleMove(null)}
                className="px-2.5 py-1.5 rounded text-sm text-left text-cime-text-primary hover:bg-[#fbfaf7]"
              >
                Sans section
              </button>
              {(sections.data ?? []).map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => handleMove(s.id)}
                  className="px-2.5 py-1.5 rounded text-sm text-left text-cime-text-primary hover:bg-[#fbfaf7] truncate"
                >
                  {s.name}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}

      <button
        type="button"
        className={`${BUTTON_CLASS} hover:bg-red-50 hover:text-red-600`}
        onClick={handleDelete}
      >
        <Trash2 className="w-4 h-4" />
        Supprimer
      </button>
      <button
        type="button"
        aria-label="Tout désélectionner"
        className={BUTTON_CLASS}
        onClick={onClear}
      >
        <X className="w-4 h-4" />
      </button>
    </div>,
    document.body,
  );
}
